// React and React Libraries
"use client";
import Dropzone from "@/components/dropzone";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import React, { useEffect, useState, useRef } from "react";
import "./animate-text.css";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
// Components
import {
  createCustomerThread,
  deleteFromCustomerDocuments,
  getPromptsInfo,
  interact,
  subscribeToCustomerDocuments,
  updateChatHistory,
  uploadFiles,
  uploadFilesToCustomerBucket,
  uploadFilesToDocumentsTable,
  fetch_uuid,
} from "@/_services/customer-chat";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowRight,
  LucideEdit,
  PlusSquare,
  Trash,
  LucideBot,
  UserRoundPen,
  RefreshCcw,
  User,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PromptDialogue from "./components/prompt-dialogue";

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { MessageMarkdown } from "@/components/messages/message-markdown";
import { useCustomerChatStore } from "@/_stores/customer-store";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import dayjs from "dayjs";
import { Checkbox } from "@/components/ui/checkbox";
import LoadingDots from "@/components/loading-dots";
import Header from "@/components/header";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { dynamicInput } from "@/lib/utils";
interface Prompt {
  title: string;
  prompt: string;
  rank: number;
  status: boolean;
}

type DynamicInputs = {
  title: string;
  prompt: string;
  keywords: string[];
};

interface ChatMessage {
  response: string;
  request: string;
  status: string;
}

interface ChatHistory {
  [key: string]: ChatMessage;
}

interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
}
const FileSideBar = ({
  user_id,
  routerThreadId,
}: {
  user_id: string;
  routerThreadId: string | null;
}) => {
  // Store functions
  const [fileDeleteLoader, setFileDeleteLoader] = useState<
    Record<string, boolean>
  >({});
  const {
    loadAllThreads,
    threads,
    loadChatHistory,
    deleteThread,
    deleteLoader,
    appendDocument,
    toggleFileStatus,
    getDocuments,
    deleteFile,
    subscribeToThreadTitle,
  } = useCustomerChatStore();
  const router = useRouter();

  useEffect(() => {
    fetchThread();
    if (routerThreadId) {
      loadChats(routerThreadId as string);
      subscribeToThreadTitle(routerThreadId as string);
    }
  }, [user_id, routerThreadId]);

  const fetchThread = () => {
    try {
      loadAllThreads(user_id);
    } catch (error) {
      toast.error("Failed to fetch threads");
    }
  };

  const loadChats = async (threadId: string) => {
    try {
      await loadChatHistory(threadId);
    } catch (error) {
      toast.error("Failed to fetch chats");
    }
  };

  const deleteAThread = async (threadId: string) => {
    try {
      await deleteThread(threadId);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const createNewThread = async () => {
    try {
      const threadResponse: any = await createCustomerThread();
      router.replace(`/customer/${threadResponse.thread_uuid}`);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const onDrop = async (event: File[], threadID: string) => {
    try {
      const files: any[] = await uploadFilesToCustomerBucket(event, threadID);

      const documentsData: any = await uploadFilesToDocumentsTable(files);
      appendDocument(threadID, documentsData);
      await uploadFiles(documentsData);
    } catch (error:any) {
      if (error.error === "InvalidKey") {
        toast.error("Invalid file name. Please rename the file");
      } else {
        console.log("Error uploading files", error);
        toast.error("Failed to upload files");
      }
    }
  };

  const uploadFile = async (threadId: string) => {
    try {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.multiple = true;
      fileInput.onchange = async (event: any) => {
        const files = event.target.files;
        if (
          files.length > 0 &&
          files.length <= 5 &&
          getDocuments(threadId).length + files.length <= 10
        ) {
          await onDrop(Array.from(files), threadId);
        } else if (files.length > 5) {
          toast.error("You can upload up to 5 files at a time.");
        } else {
          toast.error("A thread can contain a maximum of 10 files.");
        }
      };
      fileInput.click();
    } catch (error) {
      toast.error("Failed to upload files");
    }
  };

  const deleteAFile = async (
    threadId: string,
    fileId: string,
    isFailedFile: boolean = false
  ) => {
    try {
      setFileDeleteLoader((prev) => ({ ...prev, [fileId]: true }));
      await deleteFile(threadId, fileId, isFailedFile);
      toast.success("File deleted successfully!");
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete the file.");
    } finally {
      setFileDeleteLoader((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  return (
    <div className="w-full h-full  bg-zinc-900">
      <div className="py-2 pr-10 md:pr-4 flex justify-end w-full ">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Button asChild variant={"ghost"}  className="w-50" size="icon" onClick={createNewThread}>
                <LucideEdit />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Create New Thread</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <ScrollArea className="p-0 md:p-8 flex-1 w-full  h-full ">
        {Object.keys(threads)
          .reverse()
          .map((thread) => (
            <Accordion
              type="single"
              collapsible
              defaultValue={routerThreadId as any}
              key={thread}
              className="w-fit"
            >
              <AccordionItem
                value={thread}
                onClick={() => router.replace(`/customer/${threads[thread].thread_uuid}`)}
              >
                <AccordionTrigger stopNav chevronColor="text-white">
                  <div className="flex items-center gap-1 justify-between">
                    <Button
                      disabled={routerThreadId === thread}
                      variant={"ghost"}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteAThread(thread);
                      }}
                    >
                      {deleteLoader[thread] ? <LoadingSpinner /> : <Trash />}
                    </Button>
                    <div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <h1
                              onClick={() =>
                                router.replace(`/customer/${threads[thread].thread_uuid}`)
                              }
                              className="text-sm text-left truncate text-white w-60"
                            >
                              {threads[thread].title ||
                                `Untitled thread - ${thread}`}
                            </h1>
                          </TooltipTrigger>
                          <TooltipContent>
                            Click here to open thread
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <p className="text-xs text-left text-gray-500">
                        {dayjs(threads[thread].created_at).format(
                          "D MMM YYYY, HH:mm a"
                        )}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="bg-zinc-800 rounded-md px-6 md:px-12">
                  {threads[thread].customer_documents?.length ? (
                    <>
                      {threads[thread].customer_documents?.map((doc: any) => (
                        <div
                          key={doc.id}
                          className="flex w-fit items-center gap-2 p-4"
                        >
                          {["INPROGRESS", "CREATED"].includes(doc.status) ||
                          !doc.status ? (
                            <LoadingSpinner />
                          ) : doc.status ===
                            "FAILED" ? null : routerThreadId === thread ? (
                            <Checkbox
                              checked={doc.select}
                              onCheckedChange={() =>
                                toggleFileStatus(doc.id, thread)
                              }
                            />
                          ) : null}
                          <div className="flex flex-col">
                            <p className="text-sm w-40 truncate">{doc.name}</p>
                            {["INPROGRESS", "CREATED", "FAILED"].includes(
                              doc.status
                            ) && (
                              <p
                                className={`text-xs mt-1 ${
                                  doc.status === "CREATED"
                                    ? "text-yellow-500"
                                    : doc.status === "FAILED"
                                    ? "text-red-500"
                                    : "text-blue-500"
                                }`}
                              >
                                {doc.status === "INPROGRESS"
                                  ? "Processing..."
                                  : doc.status === "CREATED"
                                  ? "Uploading..."
                                  : "Upload Failed"}
                              </p>
                            )}
                          </div>

                          {routerThreadId === thread &&
                          doc.status === "COMPLETED" ? (
                            fileDeleteLoader[doc.id] ? (
                              <LoadingSpinner />
                            ) : (
                              <button
                                onClick={() => deleteAFile(thread, doc.id)}
                              >
                                <Trash size={20} color="white" />
                              </button>
                            )
                          ) : null}

                          {routerThreadId === thread &&
                          doc.status === "FAILED" ? (
                            <>
                              <button
                                onClick={() => {
                                  uploadFiles([doc]);
                                }}
                                disabled={fileDeleteLoader[doc.id]}
                              >
                                <RefreshCcw size={18} />
                              </button>

                              {fileDeleteLoader[doc.id] ? (
                                <LoadingSpinner />
                              ) : (
                                <button
                                  onClick={() => {
                                    deleteAFile(thread, doc.id, true);
                                  }}
                                >
                                  <Trash size={20} />
                                </button>
                              )}
                            </>
                          ) : null}
                        </div>
                      ))}
                      {routerThreadId === thread &&
                        threads[thread].customer_documents?.length < 10 && (
                          <Button
                            className="gap-4"
                            variant={"ghost"}
                            size={"sm"}
                            onClick={() => uploadFile(thread)}
                          >
                            <PlusSquare />
                            Upload File
                          </Button>
                        )}
                    </>
                  ) : (
                    <div className="items-center justify-center flex p-4 w-fit">
                      <h1 className="text-gray-500">No documents Uploaded</h1>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}
      </ScrollArea>
    </div>
  );
};

const ChatWindow = ({
  routerThreadId,
  user_id,
  user_email,
  setMobileMenuOpen,
}: {
  routerThreadId: string;
  user_id: string;
  user_email: string;
  setMobileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    addChatMessage,
    subscribeToThread,
    chatHistory,
    setPrompts,
    prompts,
    resetPrompts,
    getDocuments,
    threads,
    appendDocument,
    updateDocuments,
    resetChatHistory,
  } = useCustomerChatStore();

  const [message, setMessage] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPrompts, setLoadingPrompts] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);

  const doc: any[] = getDocuments(routerThreadId);
  const router = useRouter();
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const prcoessingMessage = doc.some((doc) => doc.status === "INPROGRESS");
  const [showDialogue, setShowDialogue] = useState<boolean>(false);
  const [dynamicFormInputs, setDynamicFormInputs] = useState<DynamicInputs | any>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    if (isAtBottom && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [chatHistory, isAtBottom]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      const isBottom = chatContainer.scrollHeight - chatContainer.scrollTop <= chatContainer.clientHeight + 10;
      setIsAtBottom(isBottom);
    };
    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer?.removeEventListener("scroll", handleScroll);
  }, []);

  // Mount effect for fade-in
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      resetChatHistory(); // Reset chat history on unmount
    };
  }, [resetChatHistory]);

  // Get the prompts rankings
  useEffect(() => {
    const prompts = doc
      .filter((doc) => doc.status === "COMPLETED" && doc.select == true)
      .reduce((acc, doc) => acc.concat(doc.prompts), []);
    getPromptsRankings(prompts);
  }, [routerThreadId, router, threads]);

  // Subscribe to customer documents a.k,a load prefilled prompts
  useEffect(() => {
    let subscription: any = null;
    const fetchData = async () => {
      if (routerThreadId) {
        resetPrompts();
        subscription = await subscribeToCustomerDocuments(
          routerThreadId,
          async (payload) => {
            updateDocuments(routerThreadId, payload);
            // await getPromptsRankings(payload.prompts);
          }
        );
      }
    };
    fetchData();
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [routerThreadId, router]);

  // Subscribe to chats live interaction
  useEffect(() => {
    let subscription: any = null;
    const fetchData = async () => {
      if (routerThreadId) {
        subscription = await subscribeToThread(routerThreadId);
      }
    };
    fetchData();
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [routerThreadId, router]);

  // Get the prompts rankings
  const getPromptsRankings = async (payload: Prompt[]) => {
    try {
      const prompts: Prompt[] = await getPromptsInfo(payload);
      const promptsObject: Record<number, Prompt> = {};
      prompts.forEach((prompt) => {
        promptsObject[prompt.rank] = {
          ...prompt,
        };
      });
      setPrompts(promptsObject);
    } catch (error) {
      toast.error("Failed to get prompts");
    }
  };

  // Upload files to the server
  const onDrop = async (event: File[]) => {
    try {
      setLoading(true);
      let threadID: any = routerThreadId;
      let uuid: any;
      if (threadID) {
        uuid = await fetch_uuid(threadID);
      } else {
        const threadResponse: any = await createCustomerThread();
        threadID = threadResponse.id;
        uuid = threadResponse.thread_uuid;
      }
      const files: any[] = await uploadFilesToCustomerBucket(event, threadID);
      const documentsData: any = await uploadFilesToDocumentsTable(files);
      appendDocument(threadID, documentsData);
      await uploadFiles(documentsData);
      router.replace(`/customer/${uuid}`);
    } catch (error:any) {
      if (error.error === "InvalidKey") {
        toast.error("Invalid file name. Please rename the file");
      } else {
        console.log("Error uploading files", error);
        toast.error("Failed to upload files");
      }
    }
    setLoading(false);
  };

  const handleError = (error: any) => {
    const errorMessage =
      error.response?.data?.msg || "An unexpected error occurred.";
    if (errorMessage === "Insufficient balance for this request.") {
      const error_Message = (
        <div className="p-4 bg-black text-center max-w-xs mx-auto rounded-lg shadow-md border border-gray-700">
          <span className="text-red-500 text-xl mb-2 block">❗</span>
          <p className="font-bold text-sm text-gray-200 m-0">{errorMessage}</p>
          <button
            onClick={() => {
              toast.dismiss();
              router.push("/wallet");
            }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 px-4 cursor-pointer w-full mt-4 transition-colors duration-200"
          >
            Recharge Now
          </button>
        </div>
      );
      toast.custom(error_Message);
    } else {
      toast.error("Failed to send message");
    }
  };

  // Handle interactions
  const regenerateResponse = async (
    request: string = " ",
    chatId: string,
    threadID: string
  ) => {
    try {
      const documentId = doc
        .filter((doc) => doc.select === true)
        .map((doc) => doc.id);

      if (documentId.length === 0) {
        toast.error("Please select a document");
        return;
      }
      addChatMessage(chatId, {
        id: chatId,
        response: "",
        request: request,
        status: "INPROGRESS",
      });
      await interact(request, documentId, threadID, chatId);
    } catch (error) {
      handleError(error);
    }
  };

  const handlePromptMessage = async (promptTitle: string, prompt: string, keywords?:any) => {
    setLoadingPrompts(promptTitle);
    try {
      const documentId = doc
        .filter((doc) => doc.select === true)
        .map((doc) => doc.id);

      if (documentId.length === 0) {
        toast.error("Please select a document");
        return;
      }
      const dynamicInputs: any = dynamicInput(prompt);
      if (dynamicInputs) {
        setShowDialogue(true);
        setDynamicFormInputs({ promptTitle, dynamicInputs, prompt });
        return;
      }
      const chatId = await updateChatHistory(prompt, "", routerThreadId);
      await interact(prompt, documentId, routerThreadId, chatId);
      addChatMessage(chatId, {
        id: chatId,
        response: "",
        request: prompt,
        status: "INPROGRESS",
      });
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingPrompts(null); // Reset loader
    }
  };

  const handleFreeTextSubmit = async () => {
    try {
      setLoading(true);
      const documentId = doc
        .filter((doc) => doc.select === true)
        .map((doc) => doc.id);

      if (documentId.length === 0) {
        toast.error("Please select a document");
        return;
      }
      const chatId = await updateChatHistory(message, "", routerThreadId);
      await interact(message, documentId, routerThreadId, chatId);
      addChatMessage(chatId, {
        id: chatId,
        response: "",
        request: message,
        status: "INPROGRESS",
      });
      setMessage("");
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (message.trim()) {
        handleFreeTextSubmit();
        setMessage("");
      }
    }
  };

  return (
    <div className=" w-full md:w-11/12 h-full flex flex-col bg-black overflow-hidden">
      <Header
        description="Your Documents, Our Conversations, Shared Success."
        setMobileMenuOpen={setMobileMenuOpen}
        user_email={user_email}
      />
      <div
        className="p-8 flex-1 w-full overflow-y-auto m-2"
        ref={chatContainerRef} // Attach ref to the chat container
      >
        {Object.keys(chatHistory).map((item) => (
          <div key={item}>
            <div className="flex gap-2 my-5 items-start">
              <User className="w-7 h-7 text-white-500 flex-shrink-0" />
              <div className="bg-zinc-800 text-white rounded-sm p-4 min-w-[200px] max-w-full sm:max-w-[450px] md:max-w-[300px] xl:max-w-screen-sm break-words">
                <p className="text-base">{chatHistory[item].request}</p>
              </div>
            </div>
            <div className="flex gap-2 items-start justify-end">
              <div className="bg-zinc-800 text-white rounded-sm p-4 min-w-[200px] max-w-full sm:max-w-[450px] md:max-w-[300px] xl:max-w-screen-sm">
                {chatHistory[item].response ? (
                  <MessageMarkdown content={chatHistory[item].response} />
                ) : chatHistory[item].status === "FAILED" ? (
                  <div className="flex flex-col items-center space-y-3 p-4 rounded-lg max-h-[calc(100vh-100px)] overflow-auto">
                    <p className="text-orange-600 font-medium tracking-wide text-center">
                      Something went wrong. Please try again.
                    </p>
                    <Button
                      size="sm"
                      onClick={() =>
                        regenerateResponse(
                          chatHistory[item].request,
                          item,
                          routerThreadId
                        )
                      }
                      className="p-2 rounded-lg bg-white text-black flex items-center justify-center space-x-2"
                    >
                      <RefreshCw className="h-5 w-5 text-black group-hover:text-gray-400 transition-colors duration-200" />
                      <span className="text-xs">Retry</span>
                    </Button>
                  </div>
                ) : (
                  <LoadingDots />
                )}
              </div>
              <LucideBot className="w-7 h-7 text-white-500 flex-shrink-0" />
            </div>
          </div>
        ))}
      </div>

      {showDialogue && (
        <PromptDialogue
          dynamicFormInputs={dynamicFormInputs}
          onOpen={showDialogue}
          onClose={setShowDialogue}
          promptSubmitHandler={handlePromptMessage}
        />
      )}

      <div className={isMounted ? "fade-in" : ""}>
        {!doc.length ? (
          <Dropzone
            onChange={(e: any) => onDrop(e)}
            title="How can I help you!"
            loading={loading}
            description="Upload files to kickstart your work in this thread. Analyze, compare, and collaborate effortlessly"
          />
        ) : !Object.keys(prompts).length ? (
          <div className="space-y-2 mb-8 ml-6">
            <Skeleton className="h-4 w-4/12" />
            <Skeleton className="h-4 w-8/12" />
          </div>
        ) : (
          <ScrollArea className="w-full max-w-full overflow-x-auto items-center mb-2 overflow-hidden">
            <div className="flex justify-center items-center gap-4 mr-5 ml-3">
              {Object.keys(prompts)
                .slice(0, 4)
                .map((key) => (
                  <Badge
                    key={key}
                    onClick={() =>
                      !loadingPrompts &&
                      handlePromptMessage(
                        prompts[parseInt(key)].title,
                        prompts[parseInt(key)].prompt
                      )
                    }
                    className={`bg-zinc-800 text-white cursor-pointer ${
                      loadingPrompts === prompts[parseInt(key)].title
                        ? "opacity-50"
                        : ""
                    }`}
                  >
                    <div className="flex items-center">
                      <p className="text-base whitespace-nowrap text-ellipsis">
                        {prompts[parseInt(key)].title}
                      </p>
                      {loadingPrompts === prompts[parseInt(key)].title && (
                        <div className="ml-2">
                          <LoadingSpinner />
                        </div>
                      )}
                    </div>
                  </Badge>
                ))}
            </div>
            <ScrollBar orientation="horizontal" className="h-1.25" />
          </ScrollArea>
        )}
      </div>

      <div className="flex items-center p-4 gap-3 border-t justify-center z-50">
        <Textarea
          className="w-10/12 bg-zinc-900 h-20 rounded-xl placeholder-slide-down"
          value={message}
          disabled={prcoessingMessage}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            prcoessingMessage
              ? "Please wait, uploading your file..."
              : "Message Talkument..."
          }
          onKeyDown={handleKeyDown}
        />

        {Object.keys(prompts).length ? (
          <Button
            size="sm"
            className={`ml-2 p-2 rounded ${
              !message.trim() || loading
                ? "opacity-50 cursor-not-allowed"
                : "bg-white text-black"
            }`}
            onClick={handleFreeTextSubmit}
            disabled={!message.trim() || loading}
          >
            {loading ? <LoadingSpinner /> : <ArrowRight />}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

// main component
function CustomerChat({
  user_id,
  user_email,
  threadId,
}: {
  user_id: string;
  user_email: string;
  threadId: string | null;
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  return (
    <div className="flex flex-row h-screen overflow-hidden w-full">
      <div className="hidden md:block w-max-96">
        <FileSideBar user_id={user_id} routerThreadId={threadId} />
      </div>

      <div className="md:hidden">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent
            side="left"
            className="w-full max-w-[320px] flex flex-col h-full bg-secondary p-0 focus:outline-none"
            onOpenAutoFocus={(e) => e.preventDefault()}
            closeButtonColor="text-white"
          >
            <FileSideBar user_id={user_id} routerThreadId={threadId} />
          </SheetContent>
        </Sheet>
      </div>
      <ChatWindow
        routerThreadId={threadId as any}
        user_id={user_id}
        user_email={user_email}
        setMobileMenuOpen={setMobileMenuOpen}
      />
    </div>
  );
}

export default CustomerChat;
